import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { Card, Button, PageTemplate, Badge, Modal, Input } from '../components/ui';
import { 
    FiTool, 
    FiHome, 
    FiCamera,
    FiCheck,
    FiX,
    FiAlertCircle,
    FiCheckCircle,
    FiMapPin,
    FiClock,
    FiCalendar,
    FiUser,
    FiPackage,
    FiChevronDown,
    FiChevronRight,
    FiPlus,
    FiEdit,
    FiTrash2
} from 'react-icons/fi';

// QR Scanner Component with actual camera using html5-qrcode
const QrScannerComponent = ({ onScan, onError, active }) => {
    const scannerRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        // Clear any existing scanner first
        if (scannerRef.current) {
            scannerRef.current.clear().catch(() => {});
            scannerRef.current = null;
        }

        if (!active) {
            // Clear container when inactive
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
            return;
        }

        // Small delay to ensure DOM is ready and previous scanner is fully cleaned up
        const timeoutId = setTimeout(() => {
            // Clear the container contents to remove any leftover UI
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }

            const config = {
                fps: 10,
                qrbox: { width: 250, height: 250 },
            };

            const scanner = new Html5QrcodeScanner('return-qr-reader', config, false);
            scannerRef.current = scanner;

            scanner.render(
                (decodedText /*, decodedResult */) => {
                    if (onScan) onScan(decodedText);
                },
                (errorMessage) => {
                    if (onError && typeof errorMessage === 'string' && !errorMessage.includes('QR code parse error')) {
                        onError(new Error(errorMessage));
                    }
                }
            );
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (scannerRef.current) {
                scannerRef.current.clear().catch(() => {});
                scannerRef.current = null;
            }
            // Also clear the container on unmount
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [active, onScan, onError]);

    if (!active) return null;

    return (
        <div
            ref={containerRef}
            id="return-qr-reader"
            className="rounded-lg overflow-hidden border border-primary-300 dark:border-gray-600"
        />
    );
};

/** Admin list: pending → confirmed → cancelled → completed; tie-break by booking date (oldest first for active, newest first for completed). */
const ADMIN_BOOKING_STATUS_ORDER = { pending: 0, confirmed: 1, cancelled: 2, completed: 3 };

function compareEquipmentBookingsForAdmin(a, b) {
    const oa = ADMIN_BOOKING_STATUS_ORDER[a.status] ?? 99;
    const ob = ADMIN_BOOKING_STATUS_ORDER[b.status] ?? 99;
    if (oa !== ob) return oa - ob;
    const da = new Date(a.eqm_booking_date).getTime();
    const db = new Date(b.eqm_booking_date).getTime();
    if (a.status === 'completed' && b.status === 'completed') return db - da;
    return da - db;
}

function compareVenueBookingsForAdmin(a, b) {
    const oa = ADMIN_BOOKING_STATUS_ORDER[a.status] ?? 99;
    const ob = ADMIN_BOOKING_STATUS_ORDER[b.status] ?? 99;
    if (oa !== ob) return oa - ob;
    const da = new Date(a.booking_date || a.venue_booking_date).getTime();
    const db = new Date(b.booking_date || b.venue_booking_date).getTime();
    if (a.status === 'completed' && b.status === 'completed') return db - da;
    return da - db;
}

function ManageBooking() {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('bookings');
    const [equipmentBookings, setEquipmentBookings] = useState([]);
    const [venueBookings, setVenueBookings] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [bookingLogs, setBookingLogs] = useState([]);

    // QR Return Scanner State
    const [showReturnScanner, setShowReturnScanner] = useState(false);
    const [scannedEquipment, setScannedEquipment] = useState(null);
    const [activeBookingsForEquipment, setActiveBookingsForEquipment] = useState([]);
    const [returnLoading, setReturnLoading] = useState(false);
    const [scanMessage, setScanMessage] = useState('');
    const [scanSuccess, setScanSuccess] = useState(false);
    
    // Manual Return State
    const [selectedEquipmentForReturn, setSelectedEquipmentForReturn] = useState('');
    const [selectedVenueForReturn, setSelectedVenueForReturn] = useState('');
    const [confirmedEquipmentBookings, setConfirmedEquipmentBookings] = useState([]);
    const [confirmedVenueBookings, setConfirmedVenueBookings] = useState([]);

    // Add Equipment State
    const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);
    const [addEquipmentLoading, setAddEquipmentLoading] = useState(false);
    const [newEquipmentData, setNewEquipmentData] = useState({
        eqm_name: '',
        eqm_cat: '',
        eqm_type: '',
        eqm_status: 'available',
        eqm_description: '',
        location: ''
    });

    // Edit Equipment State
    const [showEditEquipmentModal, setShowEditEquipmentModal] = useState(false);
    const [editEquipmentLoading, setEditEquipmentLoading] = useState(false);
    const [editEquipmentData, setEditEquipmentData] = useState(null);

    // Add Venue State
    const [showAddVenueModal, setShowAddVenueModal] = useState(false);
    const [addVenueLoading, setAddVenueLoading] = useState(false);
    const [newVenueData, setNewVenueData] = useState({
        venue_unit: '',
        venue_type: '',
        venue_capacity: '',
        venue_hourly_rate: 0,
        venue_status: 'available',
        venue_description: ''
    });

    // Edit Venue State
    const [showEditVenueModal, setShowEditVenueModal] = useState(false);
    const [editVenueLoading, setEditVenueLoading] = useState(false);
    const [editVenueData, setEditVenueData] = useState(null);

    // Collapsed state for equipment-by-location sections (location key -> boolean: true = expanded)
    const [expandedLocations, setExpandedLocations] = useState(() => ({}));

    // Fetch functions
    const fetchEquipmentBookings = async () => {
        const response = await fetch('/api/equipment-bookings/all', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
            const data = await response.json();
            setEquipmentBookings(data);
        }
    };

    const fetchVenueBookings = async () => {
        const response = await fetch('/api/venue-bookings/all', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
            const data = await response.json();
            setVenueBookings(data);
        }
    };

    const fetchEquipment = async () => {
        const response = await fetch('/api/equipment/all', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
            const data = await response.json();
            setEquipment(data);
        }
    };

    const fetchVenues = async () => {
        const response = await fetch('/api/venues/all', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
            const data = await response.json();
            setVenues(data);
        }
    };

    const fetchBookingLogs = async () => {
        const response = await fetch('/api/booking-logs?limit=100', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
            const data = await response.json();
            setBookingLogs(data);
        }
    };

    const fetchConfirmedEquipmentBookings = async () => {
        try {
            const response = await fetch('/api/equipment-bookings/all', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                const confirmed = data.filter(b => b.status === 'confirmed');
                setConfirmedEquipmentBookings(confirmed);
            }
        } catch (error) {
            console.error('Error fetching confirmed equipment bookings:', error);
        }
    };

    const fetchConfirmedVenueBookings = async () => {
        try {
            const response = await fetch('/api/venue-bookings/all', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                const confirmed = data.filter(b => b.status === 'confirmed');
                setConfirmedVenueBookings(confirmed);
            }
        } catch (error) {
            console.error('Error fetching confirmed venue bookings:', error);
        }
    };

    const sortedEquipmentBookingsActive = useMemo(
        () =>
            [...equipmentBookings]
                .filter((b) => b.status !== 'completed')
                .sort(compareEquipmentBookingsForAdmin),
        [equipmentBookings]
    );

    const sortedEquipmentBookingsCompleted = useMemo(
        () =>
            [...equipmentBookings]
                .filter((b) => b.status === 'completed')
                .sort(
                    (a, b) =>
                        new Date(b.eqm_booking_date).getTime() -
                        new Date(a.eqm_booking_date).getTime()
                ),
        [equipmentBookings]
    );

    const sortedVenueBookings = useMemo(
        () => [...venueBookings].sort(compareVenueBookingsForAdmin),
        [venueBookings]
    );

    // Get unique values for dropdowns
    const uniqueCategories = [...new Set(equipment.map(item => item.eqm_cat).filter(Boolean))].sort();
    const uniqueTypes = [...new Set(equipment.map(item => item.eqm_type).filter(Boolean))].sort();
    const uniqueLocations = [...new Set(equipment.map(item => item.location).filter(Boolean))].sort();

    // Combined fetch function for initial data loading.
    // silent: true skips full-page loading (use after return/status actions so the button spinner does not wait on 7 heavy API calls).
    const fetchAllData = useCallback(async (options = {}) => {
        const silent = options.silent === true;
        try {
            if (!silent) setLoading(true);
            await Promise.all([
                fetchEquipmentBookings(),
                fetchVenueBookings(),
                fetchEquipment(),
                fetchVenues(),
                fetchBookingLogs(),
                fetchConfirmedEquipmentBookings(),
                fetchConfirmedVenueBookings()
            ]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    // Load data when component mounts and user is admin
    useEffect(() => {
        if (currentUser?.User_Role === 'ATL_ADMIN') {
            fetchAllData();
        }
    }, [currentUser, fetchAllData]);

    // Handle Add Equipment
    const handleAddEquipment = async (e) => {
        e.preventDefault();
        setAddEquipmentLoading(true);
        try {
            const response = await fetch('/api/equipment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(newEquipmentData)
            });

            if (response.ok) {
                alert('Equipment added successfully!');
                setShowAddEquipmentModal(false);
                setNewEquipmentData({
                    eqm_name: '',
                    eqm_cat: '',
                    eqm_type: '',
                    eqm_status: 'available',
                    eqm_description: '',
                    location: ''
                });
                fetchEquipment(); // Refresh list
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to add equipment'}`);
            }
        } catch (error) {
            console.error('Error adding equipment:', error);
            alert('Error adding equipment');
        } finally {
            setAddEquipmentLoading(false);
        }
    };

    // Handle Edit Equipment
    const handleEditEquipment = async (e) => {
        e.preventDefault();
        setEditEquipmentLoading(true);
        try {
            const response = await fetch(`/api/equipment/${editEquipmentData._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(editEquipmentData)
            });

            if (response.ok) {
                alert('Equipment updated successfully!');
                setShowEditEquipmentModal(false);
                setEditEquipmentData(null);
                fetchEquipment(); // Refresh list
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to update equipment'}`);
            }
        } catch (error) {
            console.error('Error updating equipment:', error);
            alert('Error updating equipment');
        } finally {
            setEditEquipmentLoading(false);
        }
    };

    // Handle Delete Equipment
    const handleDeleteEquipment = async (equipmentId, equipmentName) => {
        if (!window.confirm(`Are you sure you want to delete "${equipmentName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`/api/equipment/${equipmentId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                alert('Equipment deleted successfully!');
                fetchEquipment(); // Refresh list
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to delete equipment'}`);
            }
        } catch (error) {
            console.error('Error deleting equipment:', error);
            alert('Error deleting equipment');
        }
    };

    // Open Edit Modal
    const openEditModal = (item) => {
        setEditEquipmentData({
            _id: item._id,
            eqm_name: item.eqm_name,
            eqm_cat: item.eqm_cat,
            eqm_type: item.eqm_type,
            eqm_status: item.eqm_status,
            eqm_description: item.eqm_description || '',
            location: item.location || ''
        });
        setShowEditEquipmentModal(true);
    };

    // Venue CRUD Handlers
    const handleAddVenue = async (e) => {
        e.preventDefault();
        setAddVenueLoading(true);
        try {
            const response = await fetch('/api/venues', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(newVenueData)
            });

            if (response.ok) {
                alert('Venue added successfully!');
                setShowAddVenueModal(false);
                setNewVenueData({
                    venue_unit: '',
                    venue_type: '',
                    venue_capacity: '',
                    venue_hourly_rate: 0,
                    venue_status: 'available',
                    venue_description: ''
                });
                fetchVenues(); // Refresh list
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to add venue'}`);
            }
        } catch (error) {
            console.error('Error adding venue:', error);
            alert('Error adding venue');
        } finally {
            setAddVenueLoading(false);
        }
    };

    const handleEditVenue = async (e) => {
        e.preventDefault();
        setEditVenueLoading(true);
        try {
            const response = await fetch(`/api/venues/${editVenueData._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(editVenueData)
            });

            if (response.ok) {
                alert('Venue updated successfully!');
                setShowEditVenueModal(false);
                setEditVenueData(null);
                fetchVenues(); // Refresh list
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to update venue'}`);
            }
        } catch (error) {
            console.error('Error updating venue:', error);
            alert('Error updating venue');
        } finally {
            setEditVenueLoading(false);
        }
    };

    const handleDeleteVenue = async (venueId, venueName) => {
        if (!window.confirm(`Are you sure you want to delete "${venueName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`/api/venues/${venueId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                alert('Venue deleted successfully!');
                fetchVenues(); // Refresh list
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to delete venue'}`);
            }
        } catch (error) {
            console.error('Error deleting venue:', error);
            alert('Error deleting venue');
        }
    };

    const openEditVenueModal = (venue) => {
        setEditVenueData({
            _id: venue._id,
            venue_unit: venue.venue_unit,
            venue_type: venue.venue_type,
            venue_capacity: venue.venue_capacity,
            venue_hourly_rate: venue.venue_hourly_rate,
            venue_status: venue.venue_status,
            venue_description: venue.venue_description || ''
        });
        setShowEditVenueModal(true);
    };

    // Helper function to validate MongoDB ObjectId format
    const isValidObjectId = (str) => {
        return /^[a-fA-F0-9]{24}$/.test(str);
    };

    // Handle QR Scan for Equipment Return
    const handleReturnScan = async (result) => {
        try {
            if (!result) return;

            // Normalise result into a string
            let rawText = '';

            if (typeof result === 'string') {
                rawText = result;
            } else if (Array.isArray(result)) {
                const first = result[0] || {};
                rawText = first.rawValue || first.text || JSON.stringify(first);
            } else if (typeof result === 'object') {
                rawText = result.text || result.rawValue || JSON.stringify(result);
            } else {
                console.warn('QR return scan result in unexpected format:', result);
                    return;
                }

            const trimmedResult = rawText.trim();

            let equipmentId = null;

            // Check if it's a plain ObjectId string
            if (isValidObjectId(trimmedResult)) {
                equipmentId = trimmedResult;
            } else {
                // Try parsing as JSON
                try {
                    const data = JSON.parse(trimmedResult);
                    equipmentId = data._id?.$oid || data._id || data.id || data.equipmentId;
                } catch (error) {
                    const objectIdMatch = trimmedResult.match(/[a-fA-F0-9]{24}/);
                    if (objectIdMatch) {
                        equipmentId = objectIdMatch[0];
                    }
                }
            }

            if (!equipmentId || !isValidObjectId(equipmentId)) {
                setScanSuccess(false);
                setScanMessage('Invalid QR code format. Expected a valid equipment ID.');
                setTimeout(() => setScanMessage(''), 3000);
                return;
            }

            try {
                // Fetch equipment details
                const equipmentResponse = await fetch(`/api/equipment/${equipmentId}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });

                if (equipmentResponse.ok) {
                    const foundEquipment = await equipmentResponse.json();
                    
                    // Fetch active bookings for this equipment
                    const bookingsResponse = await fetch(`/api/equipment/${equipmentId}/active-bookings`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });

                    if (bookingsResponse.ok) {
                        const activeBookings = await bookingsResponse.json();
                        
                        // Check if equipment has any confirmed bookings
                        if (activeBookings.length === 0) {
                            setScanSuccess(false);
                            setScanMessage(`⚠️ "${foundEquipment.eqm_name}" has no active bookings. This equipment is not currently borrowed.`);
                            setScannedEquipment(null);
                            setActiveBookingsForEquipment([]);
                        } else {
                            // Equipment has active bookings - allow return
                            setScannedEquipment(foundEquipment);
                            setActiveBookingsForEquipment(activeBookings);
                            setScanSuccess(true);
                            setScanMessage(`✓ Found: ${foundEquipment.eqm_name} - ${activeBookings.length} active booking(s). Select below to confirm return.`);
                        }
                    }
                } else {
                    setScanSuccess(false);
                    setScanMessage('❌ Equipment not found in database.');
                    setScannedEquipment(null);
                    setActiveBookingsForEquipment([]);
                }
            } catch (error) {
                console.error('Error processing QR scan:', error);
                setScanSuccess(false);
                setScanMessage('Error processing QR code.');
            }
            
            setTimeout(() => setScanMessage(''), 5000);
        } catch (err) {
            console.error('Unexpected error in handleReturnScan:', err, 'raw result:', result);
            setScanSuccess(false);
            setScanMessage('Unexpected error while reading QR code.');
            setTimeout(() => setScanMessage(''), 3000);
        }
    };

    // Handle Equipment Return Submission for a specific booking
    const handleReturnSubmit = async (bookingId) => {
        setReturnLoading(true);
        try {
            const response = await fetch(`/api/equipment-return/${bookingId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ notes: 'Equipment returned via QR scan' })
            });

            if (response.ok) {
                setScanSuccess(true);
                setScanMessage('✅ Equipment returned successfully! Status: Confirmed(Unavailable) → Available(Returned)');

                // Update local state
                setActiveBookingsForEquipment(prev => prev.filter(b => b._id !== bookingId));

                fetchAllData({ silent: true }).catch((e) => console.error('Refresh after return:', e));
                
                // If no more bookings, clear scanner
                if (activeBookingsForEquipment.length <= 1) {
                    setTimeout(() => {
                        setScannedEquipment(null);
                        setShowReturnScanner(false);
                        setScanMessage('');
                    }, 3000);
                }
            } else {
                const error = await response.json();
                setScanSuccess(false);
                setScanMessage(`❌ ${error.message || 'Error confirming return.'}`);
            }
        } catch (error) {
            console.error('Error processing return:', error);
            setScanSuccess(false);
            setScanMessage('❌ Error processing equipment return.');
        } finally {
            setReturnLoading(false);
            setTimeout(() => setScanMessage(''), 5000);
        }
    };

    // Handle Manual Equipment Return
    const handleManualEquipmentReturn = async () => {
        if (!selectedEquipmentForReturn) {
            alert('Please select an equipment booking to return');
            return;
        }

        setReturnLoading(true);
        try {
            const response = await fetch('/api/equipment-return/manual', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ 
                    booking_id: selectedEquipmentForReturn,
                    notes: 'Equipment returned manually via admin panel'
                })
            });

            if (response.ok) {
                alert('✅ Equipment returned successfully!\n\nStatus changed: Confirmed(Unavailable) → Available(Returned)');
                setSelectedEquipmentForReturn('');
                fetchAllData({ silent: true }).catch((e) => console.error('Refresh after manual return:', e));
            } else {
                const error = await response.json().catch(() => ({}));
                const detail = error.details ? `\n\n${error.details}` : '';
                alert(`❌ Error: ${error.message || 'Failed to return equipment'}${detail}`);
            }
        } catch (error) {
            console.error('Error processing manual return:', error);
            alert('❌ Error processing equipment return.');
        } finally {
            setReturnLoading(false);
        }
    };

    // Handle Manual Venue Return
    const handleManualVenueReturn = async () => {
        if (!selectedVenueForReturn) {
            alert('Please select a venue booking to return');
            return;
        }

        setReturnLoading(true);
        try {
            const response = await fetch(`/api/venue-return/${selectedVenueForReturn}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ 
                    notes: 'Venue returned manually via admin panel'
                })
            });

            if (response.ok) {
                alert('Venue return confirmed successfully!');
                setSelectedVenueForReturn('');
                fetchAllData({ silent: true }).catch((e) => console.error('Refresh after venue return:', e));
            } else {
                const error = await response.json();
                alert(`Error: ${error.message || 'Failed to return venue'}`);
            }
        } catch (error) {
            console.error('Error processing manual venue return:', error);
            alert('Error processing venue return.');
        } finally {
            setReturnLoading(false);
        }
    };

    // Handle Scanner Errors
    const handleScannerError = (err) => {
        console.error('QR Scanner Error:', err);
        if (err.name === 'NotAllowedError') {
            setScanMessage('Camera access denied. Please allow camera access.');
        } else if (err.name === 'NotFoundError') {
            setScanMessage('No camera detected.');
        }
        setScanSuccess(false);
    };

    // Update booking status (confirm or cancel)
    const updateBookingStatus = async (type, bookingId, newStatus) => {
        try {
            const endpoint = type === 'equipment' ? 'equipment-bookings' : 'venue-bookings';
            const response = await fetch(`/api/${endpoint}/${bookingId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                fetchAllData();
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Error updating status');
        }
    };

    // Format date helper
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    // Format time helper
    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Get status badge variant
    const getStatusVariant = (status) => {
        const variants = {
            'pending': 'warning',
            'confirmed': 'primary',
            'cancelled': 'error',
            'completed': 'success',
            'finished': 'success',
            'available': 'success',
            'maintenance': 'warning',
            'out_of_order': 'error'
        };
        return variants[status?.toLowerCase()] || 'neutral';
    };

    // Group equipment by location
    const groupEquipmentByLocation = () => {
        const grouped = {};
        equipment.forEach(item => {
            const location = item.location || 'Unassigned';
            if (!grouped[location]) {
                grouped[location] = [];
            }
            grouped[location].push(item);
        });
        return grouped;
    };

    // Get equipment status badge variant
    const getEquipmentStatusVariant = (status) => {
        const variants = {
            'available': 'success',
            'maintenance': 'warning',
            'out_of_order': 'error'
        };
        return variants[status] || 'neutral';
    };

    // Render booking status action buttons
    const renderStatusActions = (type, booking) => {
        if (booking.status === 'completed' || booking.status === 'cancelled') {
            return (
                <Badge variant={getStatusVariant(booking.status)}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </Badge>
            );
        }

        return (
            <div className="flex items-center gap-2">
                <Badge variant={getStatusVariant(booking.status)} className="mr-2">
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </Badge>
                {booking.status === 'pending' && (
                    <>
                        <button
                            onClick={() => updateBookingStatus(type, booking._id, 'confirmed')}
                            className="p-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors"
                            title="Confirm Booking"
                        >
                            <FiCheck className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => updateBookingStatus(type, booking._id, 'cancelled')}
                            className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                            title="Cancel Booking"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </>
                )}
                {booking.status === 'confirmed' && (
                    <button
                        onClick={() => updateBookingStatus(type, booking._id, 'cancelled')}
                        className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
                        title="Cancel Booking"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                )}
            </div>
        );
    };

    // Render QR Return Scanner Section
    const renderQrReturnSection = () => {
        // Check if manual return is in use (equipment selected but scanner not active)
        const isManualReturnMode = selectedEquipmentForReturn && !showReturnScanner;
        const isScannerMode = showReturnScanner || scannedEquipment;

        return (
            <Card className="mb-6">
                <Card.Header>
                    <Card.Title className="text-xl font-serif flex items-center">
                        <FiCamera className="mr-2" />
                        Equipment Return
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    <div className="space-y-4">
                        {/* Mode Selection */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                            <p className="text-sm text-blue-900 dark:text-blue-200 mb-2 font-medium">Choose Return Method:</p>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    type="button"
                                    variant={showReturnScanner ? "primary" : "ghost"}
                                    className="justify-center"
                                    onClick={() => {
                                        setShowReturnScanner(!showReturnScanner);
                                        if (!showReturnScanner) {
                                            // Switching to scanner mode
                                            setSelectedEquipmentForReturn('');
                                            setScannedEquipment(null);
                                            setActiveBookingsForEquipment([]);
                                            setScanMessage('');
                                        }
                                    }}
                                    icon={FiCamera}
                                    disabled={isManualReturnMode}
                                >
                                    {showReturnScanner ? 'Close Scanner' : 'QR Scanner'}
                                </Button>
                                <Button
                                    type="button"
                                    variant={isManualReturnMode ? "primary" : "ghost"}
                                    className="justify-center"
                                    onClick={() => {
                                        if (showReturnScanner) {
                                            // Disable scanner when switching to manual
                                            setShowReturnScanner(false);
                                            setScannedEquipment(null);
                                            setActiveBookingsForEquipment([]);
                                            setScanMessage('');
                                        }
                                    }}
                                    icon={FiPackage}
                                    disabled={isScannerMode}
                                >
                                    Manual Select
                                </Button>
                            </div>
                        </div>

                    {/* Scan Message */}
                    {scanMessage && (
                        <div className={`p-3 rounded-lg flex items-center gap-2 ${
                            scanSuccess 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800' 
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                        }`}>
                            {scanSuccess ? <FiCheckCircle className="flex-shrink-0" /> : <FiAlertCircle className="flex-shrink-0" />}
                            <span className="text-sm font-medium">{scanMessage}</span>
                        </div>
                    )}

                    {/* Scanner */}
                    {showReturnScanner && (
                        <div className="space-y-4">
                            <QrScannerComponent
                                active={showReturnScanner}
                                onScan={handleReturnScan}
                                onError={handleScannerError}
                            />
                            <p className="text-xs text-center text-primary-500 dark:text-gray-400">
                                Point camera at equipment QR code to confirm return
                            </p>
                        </div>
                    )}

                    {/* Scanned Equipment Details */}
                            {scannedEquipment && (
                        <div className="p-4 bg-primary-50 dark:bg-gray-700/50 rounded-lg border border-primary-200 dark:border-gray-600">
                            <h4 className="font-serif font-semibold text-primary-900 dark:text-white mb-3 flex items-center">
                                <FiPackage className="mr-2" />
                                Scanned Equipment
                                            </h4>
                            <div className="space-y-2 mb-4">
                                <p className="text-sm text-primary-700 dark:text-gray-300">
                                                <strong>Name:</strong> {scannedEquipment.eqm_name}
                                            </p>
                                <p className="text-sm text-primary-700 dark:text-gray-300">
                                                <strong>Category:</strong> {scannedEquipment.eqm_cat} - {scannedEquipment.eqm_type}
                                            </p>
                                <p className="text-sm text-primary-700 dark:text-gray-300">
                                    <strong>Location:</strong> {scannedEquipment.location || 'Unassigned'}
                                </p>
                                <p className="text-sm text-primary-700 dark:text-gray-300">
                                    <strong>Status:</strong>{' '}
                                    <Badge variant={getEquipmentStatusVariant(scannedEquipment.eqm_status)}>
                                        {scannedEquipment.eqm_status}
                                    </Badge>
                                </p>
                            </div>

                            {/* Active Bookings for this Equipment */}
                            {activeBookingsForEquipment.length > 0 ? (
                                <div className="space-y-3">
                                    <h5 className="text-sm font-semibold text-primary-800 dark:text-gray-200">
                                        Active Bookings ({activeBookingsForEquipment.length})
                                    </h5>
                                    {activeBookingsForEquipment.map((booking) => (
                                        <div 
                                            key={booking._id} 
                                            className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-primary-100 dark:border-gray-600"
                                        >
                                            {/* Borrower Info */}
                                            <div className="mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FiUser className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                    <p className="text-sm font-semibold text-primary-900 dark:text-white">
                                                        {booking.user?.First_Name} {booking.user?.Last_Name}
                                                    </p>
                                                </div>
                                                <p className="text-xs text-primary-600 dark:text-gray-400 ml-6">
                                                    UID: {booking.user?.User_ID || 'N/A'}
                                                </p>
                                            </div>
                                            
                                            {/* Booking Details */}
                                            <div className="space-y-1 mb-3">
                                                <p className="text-sm text-primary-700 dark:text-gray-300 flex items-center gap-1">
                                                    <FiCalendar className="w-4 h-4" />
                                                    {formatDate(booking.eqm_booking_date)} at {booking.eqm_booking_time}
                                                </p>
                                                <p className="text-xs text-primary-600 dark:text-gray-400">
                                                    Duration: {booking.eqm_booking_duration}h
                                                </p>
                                                <Badge variant={getStatusVariant(booking.status)} className="text-xs">
                                                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                                </Badge>
                                            </div>
                                            
                                            <Button
                                                onClick={() => handleReturnSubmit(booking._id)}
                                                variant="success"
                                                className="w-full"
                                                loading={returnLoading}
                                                icon={FiCheck}
                                            >
                                                Confirm Return
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Manual Return Section */}
                    {!isScannerMode && (
                        <div className="mt-2">
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                <h4 className="font-serif font-medium text-primary-900 dark:text-white mb-3 flex items-center">
                                    <FiPackage className="mr-2" />
                                    Select Confirmed Booking
                                </h4>
                                <div className="space-y-3">
                                    <select
                                        value={selectedEquipmentForReturn}
                                        onChange={(e) => setSelectedEquipmentForReturn(e.target.value)}
                                        className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-300"
                                        disabled={isScannerMode}
                                    >
                                        <option value="">Select a confirmed booking...</option>
                                        {confirmedEquipmentBookings.map((booking) => (
                                            <option key={booking._id} value={booking._id}>
                                                {booking.equipment?.eqm_name} - {formatDate(booking.eqm_booking_date)} at {booking.eqm_booking_time} ({booking.user?.First_Name} {booking.user?.Last_Name})
                                            </option>
                                        ))}
                                    </select>
                                    {confirmedEquipmentBookings.length === 0 && (
                                        <p className="text-xs text-primary-500 dark:text-gray-400">
                                            No confirmed equipment bookings available for return
                                        </p>
                                    )}
                                    <Button
                                        onClick={handleManualEquipmentReturn}
                                        variant="primary"
                                        className="w-full"
                                        loading={returnLoading}
                                        disabled={!selectedEquipmentForReturn || returnLoading || isScannerMode}
                                        icon={FiCheck}
                                    >
                                        Mark Equipment as Returned
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Disabled state message for scanner mode */}
                    {isScannerMode && !scannedEquipment && (
                        <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Manual return is disabled while scanner is active. Close the scanner to use manual return.
                            </p>
                        </div>
                    )}
                </div>
            </Card.Content>
        </Card>
        );
    };

    // Toggle equipment location section expanded/collapsed
    const toggleLocationExpanded = (location) => {
        setExpandedLocations((prev) => ({
            ...prev,
            [location]: !prev[location]
        }));
    };

    // Render Equipment List grouped by location (foldable sections)
    const renderEquipmentByLocation = () => {
        const groupedEquipment = groupEquipmentByLocation();
        const locations = Object.keys(groupedEquipment).sort();

        return (
            <Card>
                <Card.Header>
                    <div className="flex justify-between items-center">
                        <Card.Title className="text-xl font-serif flex items-center">
                            <FiMapPin className="mr-2" />
                            Equipment by Location
                        </Card.Title>
                        <Button
                            onClick={() => setShowAddEquipmentModal(true)}
                            variant="primary"
                            size="sm"
                            icon={FiPlus}
                        >
                            Add Equipment
                        </Button>
                    </div>
                </Card.Header>
                <Card.Content>
                    <div className="space-y-2">
                        {locations.map((location) => {
                            const isExpanded = expandedLocations[location] !== false;
                            return (
                            <div key={location} className="border border-primary-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                {/* Foldable bar — always visible */}
                                <button
                                    type="button"
                                    onClick={() => toggleLocationExpanded(location)}
                                    className="w-full flex items-center justify-between px-4 py-3 bg-primary-50 dark:bg-gray-700/50 hover:bg-primary-100 dark:hover:bg-gray-700 text-left transition-colors"
                                >
                                    <div className="flex items-center">
                                        {isExpanded ? (
                                            <FiChevronDown className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0" />
                                        ) : (
                                            <FiChevronRight className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0" />
                                        )}
                                        <FiMapPin className="mr-2 text-primary-500 flex-shrink-0" />
                                        <span className="text-lg font-serif font-semibold text-primary-800 dark:text-white">
                                            {location}
                                        </span>
                                        <span className="ml-2 text-sm font-normal text-primary-500 dark:text-gray-400">
                                            ({groupedEquipment[location].length} items)
                                        </span>
                                    </div>
                                </button>
                                {/* Content — shown when expanded */}
                                {isExpanded && (
                                <div className="p-4 pt-0 border-t border-primary-200 dark:border-gray-600">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {groupedEquipment[location].map((item) => {
                                        // Find active bookings for this equipment
                                        const activeBookings = equipmentBookings.filter(
                                            b => b.equipment?._id === item._id && 
                                                (b.status === 'pending' || b.status === 'confirmed')
                                        );
                                        const activeBookingCount = activeBookings.length;
                                        
                                        // Get the most recent active booking status
                                        const mostRecentBooking = activeBookings.sort((a, b) => 
                                            new Date(b.createdAt) - new Date(a.createdAt)
                                        )[0];
                                        
                                        // Determine which status to show
                                        const displayStatus = mostRecentBooking 
                                            ? mostRecentBooking.status 
                                            : item.eqm_status;
                                        
                                        // Use the consolidated getStatusVariant function
                                        const displayVariant = getStatusVariant(displayStatus);

                                        return (
                                            <div 
                                                key={item._id} 
                                                className="p-4 bg-primary-50 dark:bg-gray-700/50 rounded-lg border border-primary-200 dark:border-gray-600"
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-serif font-medium text-primary-900 dark:text-white">
                                                        {item.eqm_name}
                                                    </h4>
                                                    <Badge variant={displayVariant}>
                                                        {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                                    </Badge>
                                                </div>
                                                <div className="space-y-1 text-sm text-primary-600 dark:text-gray-400">
                                                    <p><strong>Category:</strong> {item.eqm_cat}</p>
                                                    <p><strong>Type:</strong> {item.eqm_type}</p>
                                                    {item.eqm_description && (
                                                        <p className="text-xs mt-2 text-primary-500 dark:text-gray-500">
                                                            {item.eqm_description}
                                                        </p>
                                                    )}
                                                    {activeBookingCount > 0 && (
                                                        <p className="mt-2">
                                                            <Badge variant="warning">
                                                                {activeBookingCount} active booking(s) - Unavailable
                                                            </Badge>
                                                        </p>
                                                    )}
                                                    {(item.availability_status === 'unavailable' || item.eqm_status !== 'available') && activeBookingCount === 0 && (
                                                        <p className="mt-2">
                                                            <Badge variant={item.eqm_status === 'maintenance' ? 'warning' : 'error'}>
                                                                {item.eqm_status}
                                                            </Badge>
                                                        </p>
                                                    )}
                                                </div>
                                                <p className="text-xs text-primary-400 dark:text-gray-500 mt-2">
                                                    ID: {item._id}
                                                </p>
                                                <div className="flex gap-2 mt-3 pt-3 border-t border-primary-200 dark:border-gray-600">
                                                    <Button
                                                        onClick={() => openEditModal(item)}
                                                        variant="secondary"
                                                        size="sm"
                                                        icon={FiEdit}
                                                        className="flex-1"
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleDeleteEquipment(item._id, item.eqm_name)}
                                                        variant="error"
                                                        size="sm"
                                                        icon={FiTrash2}
                                                        className="flex-1"
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                </div>
                                )}
                            </div>
                        );
                        })}
                    </div>
                </Card.Content>
            </Card>
        );
    };

    // Render Bookings List
    const renderBookingsList = () => {
        const borrowerLabel = (u) =>
            (u?.full_name && String(u.full_name).trim()) ||
            `${u?.First_Name || ''} ${u?.Last_Name || ''}`.trim() ||
            '—';

        const renderEquipmentBookingCard = (booking) => (
                            <div
                                key={booking._id}
                                className="p-4 bg-primary-50 dark:bg-gray-700/50 rounded-lg border border-primary-200 dark:border-gray-600"
                            >
                                <div className="flex flex-col gap-4">
                                    {/* Header Section */}
                                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                                        <div className="flex-1 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <FiTool className="text-primary-500" />
                                                <h4 className="font-serif font-medium text-primary-900 dark:text-white">
                                                    {booking.equipment?.eqm_name || 'Unknown Equipment'}
                                                </h4>
                                            </div>
                                            
                                            {/* Basic Info Grid */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-primary-600 dark:text-gray-400">
                                                <p className="flex items-center gap-1">
                                                    <FiCalendar className="w-4 h-4" />
                                                    {formatDate(booking.eqm_booking_date)}
                                                </p>
                                                <p className="flex items-center gap-1">
                                                    <FiClock className="w-4 h-4" />
                                                    {booking.eqm_booking_time} ({booking.eqm_booking_duration}h)
                                                </p>
                                                <p className="flex items-center gap-1">
                                                    <FiUser className="w-4 h-4" />
                                                    {borrowerLabel(booking.user)}
                                                </p>
                                                <p className="flex items-center gap-1">
                                                    <FiMapPin className="w-4 h-4" />
                                                    {booking.equipment?.location || 'Unassigned'}
                                                </p>
                                            </div>
                                            
                                            {/* Extended Booking Information */}
                                            {(booking.quantity || booking.affiliation || booking.remarks || booking.phone_number || booking.event_funding_source || booking.course_code || booking.booking_nature || (booking.equipment_ids && booking.equipment_ids.length > 1)) && (
                                                <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-md border border-primary-200 dark:border-gray-600">
                                                    <h5 className="text-xs font-serif font-semibold text-primary-900 dark:text-white mb-2 uppercase">
                                                        Booking Details
                                                    </h5>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-primary-600 dark:text-gray-400">
                                                        {booking.quantity && booking.quantity > 1 && (
                                                            <p><strong>Quantity:</strong> {booking.quantity} units</p>
                                                        )}
                                                        {booking.affiliation && (
                                                            <p><strong>Affiliation:</strong> {booking.affiliation}</p>
                                                        )}
                                                        {booking.phone_number && (
                                                            <p><strong>Phone:</strong> {booking.phone_number}</p>
                                                        )}
                                                        {booking.event_funding_source && (
                                                            <p><strong>Funding Source:</strong> {booking.event_funding_source}</p>
                                                        )}
                                                        {booking.course_code && (
                                                            <p><strong>Course/Project:</strong> {booking.course_code}</p>
                                                        )}
                                                        {booking.account_code && (
                                                            <p><strong>Account Code:</strong> {booking.account_code}</p>
                                                        )}
                                                        {booking.booking_nature && (
                                                            <p><strong>Nature:</strong> {booking.booking_nature}</p>
                                                        )}
                                                    </div>
                                                    {booking.remarks && (
                                                        <div className="mt-2 pt-2 border-t border-primary-100 dark:border-gray-700">
                                                            <p className="text-xs"><strong>Remarks:</strong></p>
                                                            <p className="text-xs text-primary-600 dark:text-gray-400 mt-1">{booking.remarks}</p>
                                                        </div>
                                                    )}
                                                    {/* Display assigned equipment units for mass bookings */}
                                                    {booking.equipment_ids && booking.equipment_ids.length > 1 && (
                                                        <div className="mt-2 pt-2 border-t border-primary-100 dark:border-gray-700">
                                                            <p className="text-xs font-semibold mb-2"><strong>Assigned Units ({booking.equipment_ids.length}):</strong></p>
                                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                                                {booking.equipment_ids.map((eqm, idx) => (
                                                                    <div key={idx} className="text-xs bg-primary-100 dark:bg-gray-700 px-2 py-1 rounded">
                                                                        {eqm?.eqm_serial_number || eqm?.eqm_name || `Unit ${idx + 1}`}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center">
                                            {renderStatusActions('equipment', booking)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );

        return (
        <Card>
            <Card.Header>
                <Card.Title className="text-xl font-serif flex items-center">
                    <FiCalendar className="mr-2" />
                    Equipment Bookings
                </Card.Title>
            </Card.Header>
            <Card.Content>
                {equipmentBookings.length === 0 ? (
                    <div className="text-center py-8">
                        <FiAlertCircle className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
                        <p className="font-literary text-primary-500 dark:text-gray-400">No bookings found</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="space-y-4">
                            {sortedEquipmentBookingsActive.length === 0 && sortedEquipmentBookingsCompleted.length > 0 ? (
                                <p className="text-sm text-primary-600 dark:text-gray-400 font-literary">
                                    No active equipment bookings (only completed records below).
                                </p>
                            ) : (
                                sortedEquipmentBookingsActive.map(renderEquipmentBookingCard)
                            )}
                        </div>
                        {sortedEquipmentBookingsCompleted.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-primary-200 dark:border-gray-600">
                                <h3 className="text-sm font-serif font-semibold text-primary-700 dark:text-gray-300">
                                    Completed (returned)
                                </h3>
                                <div className="space-y-4">
                                    {sortedEquipmentBookingsCompleted.map(renderEquipmentBookingCard)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card.Content>
        </Card>
        );
    };

    // Render Booking Logs with clear status flow
    const renderBookingLogs = () => {
        // Helper function to get status with availability
        const getStatusWithAvailability = (status, isFrom = false) => {
            switch(status) {
                case 'pending':
                    return 'Pending (Unavailable)';
                case 'confirmed':
                    return 'Confirmed (Unavailable)';
                case 'completed':
                case 'returned':
                    return 'Available (Returned)';
                case 'cancelled':
                    return 'Cancelled (Available)';
                default:
                    return isFrom ? 'Available' : status;
            }
        };

        return (
            <Card>
                <Card.Header>
                    <Card.Title className="text-xl font-serif flex items-center">
                        <FiClock className="mr-2" />
                        Booking Activity Log
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    {bookingLogs.length === 0 ? (
                        <div className="text-center py-8">
                            <FiAlertCircle className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
                            <p className="font-literary text-primary-500 dark:text-gray-400">No activity logs found</p>
                        </div>
                    ) : (
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                            {bookingLogs.map((log) => {
                                const isEquipment = log.booking_type === 'equipment';
                                const itemName = isEquipment 
                                    ? log.booking_details?.equipment_name || log.equipment_id?.eqm_name || 'Equipment'
                                    : log.booking_details?.venue_unit || log.venue_id?.venue_unit || 'Venue';
                                const statusChangeTime = log.status_changed_at || log.createdAt;
                                
                                // Get user info
                                const borrowerName = log.user?.full_name || 
                                                    `${log.user?.First_Name || ''} ${log.user?.Last_Name || ''}`.trim() || 
                                                    'Unknown User';
                                const borrowerUID = log.user?.User_ID || 'N/A';
                                
                                // Get admin info
                                const adminName = log.admin?.full_name || 
                                                 `${log.admin?.First_Name || ''} ${log.admin?.Last_Name || ''}`.trim() || 
                                                 null;
                                
                                // Previous and new status with availability
                                const fromStatus = getStatusWithAvailability(log.previous_status, true);
                                const toStatus = getStatusWithAvailability(log.new_status);
                                
                                return (
                                    <div 
                                        key={log._id} 
                                        className="p-4 bg-primary-50 dark:bg-gray-700/50 rounded-lg border-l-4 border-primary-500"
                                    >
                                        {/* Header: Item name and timestamp */}
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                {isEquipment ? <FiTool className="text-primary-500 w-5 h-5" /> : <FiHome className="text-primary-500 w-5 h-5" />}
                                                <h4 className="text-base font-semibold text-primary-900 dark:text-white">
                                                    {itemName}
                                                </h4>
                                                <Badge variant={log.booking_type === 'equipment' ? 'neutral' : 'success'} className="text-xs">
                                                    {log.booking_type === 'equipment' ? 'Equipment' : 'Venue'}
                                                </Badge>
                                            </div>
                                            <span className="text-xs text-primary-400 dark:text-gray-500 whitespace-nowrap">
                                                {formatDateTime(statusChangeTime)}
                                            </span>
                                        </div>

                                        {/* Status Flow - Large and Clear */}
                                        <div className="bg-white dark:bg-gray-800 p-3 rounded-md mb-3">
                                            <div className="flex items-center gap-2 justify-center">
                                                <div className="text-center flex-1">
                                                    <Badge variant={getStatusVariant(log.previous_status || 'available')} className="text-sm px-3 py-1">
                                                        {fromStatus}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-1 text-primary-500 dark:text-gray-400">
                                                    <div className="h-px w-6 bg-primary-300 dark:bg-gray-600"></div>
                                                    <span className="text-lg">→</span>
                                                    <div className="h-px w-6 bg-primary-300 dark:bg-gray-600"></div>
                                                </div>
                                                <div className="text-center flex-1">
                                                    <Badge variant={getStatusVariant(log.new_status)} className="text-sm px-3 py-1">
                                                        {toStatus}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Borrower Information - Prominent */}
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md mb-3 border border-blue-200 dark:border-blue-800">
                                            <div className="flex items-start gap-2">
                                                <FiUser className="text-blue-600 dark:text-blue-400 w-4 h-4 mt-0.5" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                                                        Borrower: {borrowerName}
                                                    </p>
                                                    <p className="text-xs text-blue-700 dark:text-blue-300">
                                                        UID: {borrowerUID}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action and Admin */}
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2 text-primary-700 dark:text-gray-300">
                                                <span className="font-medium">Action:</span>
                                                <span className="capitalize">{log.action}</span>
                                                {adminName && (
                                                    <>
                                                        <span className="text-primary-400 dark:text-gray-500">•</span>
                                                        <span className="font-medium">Admin:</span>
                                                        <span>{adminName}</span>
                                                    </>
                                                )}
                                            </div>
                                            
                                            {/* Booking Details */}
                                            <div className="text-xs text-primary-600 dark:text-gray-400 pt-2 border-t border-primary-200 dark:border-gray-600">
                                                {isEquipment ? (
                                                    <div className="space-y-1">
                                                        {log.booking_details?.eqm_booking_date && (
                                                            <p className="flex items-center gap-1">
                                                                <FiCalendar className="w-3 h-3" />
                                                                <span>{formatDate(log.booking_details.eqm_booking_date)} at {log.booking_details.eqm_booking_time} ({log.booking_details.eqm_booking_duration}h)</span>
                                                            </p>
                                                        )}
                                                        {log.booking_details?.equipment_category && (
                                                            <p className="flex items-center gap-1">
                                                                <FiPackage className="w-3 h-3" />
                                                                <span>{log.booking_details.equipment_category} - {log.booking_details.equipment_type}</span>
                                                            </p>
                                                        )}
                                                        {log.booking_details?.location && (
                                                            <p className="flex items-center gap-1">
                                                                <FiMapPin className="w-3 h-3" />
                                                                <span>{log.booking_details.location}</span>
                                                            </p>
                                                        )}
                                                        {/* Extended Equipment Booking Details */}
                                                        {log.booking_details?.quantity && log.booking_details.quantity > 1 && (
                                                            <p><strong>Quantity:</strong> {log.booking_details.quantity}</p>
                                                        )}
                                                        {log.booking_details?.affiliation && (
                                                            <p><strong>Affiliation:</strong> {log.booking_details.affiliation}</p>
                                                        )}
                                                        {log.booking_details?.phone_number && (
                                                            <p><strong>Phone:</strong> {log.booking_details.phone_number}</p>
                                                        )}
                                                        {log.booking_details?.event_funding_source && (
                                                            <p><strong>Funding:</strong> {log.booking_details.event_funding_source}</p>
                                                        )}
                                                        {log.booking_details?.course_code && (
                                                            <p><strong>Course/Project:</strong> {log.booking_details.course_code}</p>
                                                        )}
                                                        {log.booking_details?.account_code && (
                                                            <p><strong>Account Code:</strong> {log.booking_details.account_code}</p>
                                                        )}
                                                        {log.booking_details?.booking_nature && (
                                                            <p><strong>Nature:</strong> {log.booking_details.booking_nature}</p>
                                                        )}
                                                        {log.booking_details?.remarks && (
                                                            <p className="mt-1 pt-1 border-t border-primary-100 dark:border-gray-700">
                                                                <strong>Remarks:</strong> {log.booking_details.remarks}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1">
                                                        {log.booking_details?.venue_booking_date && (
                                                            <p className="flex items-center gap-1">
                                                                <FiCalendar className="w-3 h-3" />
                                                                <span>{formatDate(log.booking_details.venue_booking_date)} at {log.booking_details.venue_booking_time} ({log.booking_details.venue_booking_duration}h)</span>
                                                            </p>
                                                        )}
                                                        {log.booking_details?.venue_type && (
                                                            <p className="flex items-center gap-1">
                                                                <FiHome className="w-3 h-3" />
                                                                <span>{log.booking_details.venue_type} (Capacity: {log.booking_details.venue_capacity})</span>
                                                            </p>
                                                        )}
                                                        {log.booking_details?.total_pay > 0 && (
                                                            <p className="flex items-center gap-1">
                                                                <span>💰</span>
                                                                <span>HKD ${log.booking_details.total_pay}</span>
                                                            </p>
                                                        )}
                                                        {/* Extended Venue Booking Details */}
                                                        {log.booking_details?.quantity && log.booking_details.quantity > 1 && (
                                                            <p><strong>Quantity:</strong> {log.booking_details.quantity}</p>
                                                        )}
                                                        {log.booking_details?.reserve_small_room && (
                                                            <p><strong>Small Computer Room:</strong> Reserved (3 stations)</p>
                                                        )}
                                                        {log.booking_details?.reserve_large_room && (
                                                            <p><strong>Large Computer Room:</strong> Reserved (5 stations)</p>
                                                        )}
                                                        {log.booking_details?.affiliation && (
                                                            <p><strong>Affiliation:</strong> {log.booking_details.affiliation}</p>
                                                        )}
                                                        {log.booking_details?.phone_number && (
                                                            <p><strong>Phone:</strong> {log.booking_details.phone_number}</p>
                                                        )}
                                                        {log.booking_details?.event_funding_source && (
                                                            <p><strong>Funding:</strong> {log.booking_details.event_funding_source}</p>
                                                        )}
                                                        {log.booking_details?.course_code && (
                                                            <p><strong>Course/Project:</strong> {log.booking_details.course_code}</p>
                                                        )}
                                                        {log.booking_details?.account_code && (
                                                            <p><strong>Account Code:</strong> {log.booking_details.account_code}</p>
                                                        )}
                                                        {log.booking_details?.booking_nature && (
                                                            <p><strong>Nature:</strong> {log.booking_details.booking_nature}</p>
                                                        )}
                                                        {log.booking_details?.remarks && (
                                                            <p className="mt-1 pt-1 border-t border-primary-100 dark:border-gray-700">
                                                                <strong>Remarks:</strong> {log.booking_details.remarks}
                                                            </p>
                                                        )}
                                                        {log.booking_details?.booking_desc && (
                                                            <p className="mt-1 pt-1 border-t border-primary-100 dark:border-gray-700">
                                                                <strong>Description:</strong> {log.booking_details.booking_desc}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {log.notes && (
                                                <p className="text-xs text-primary-600 dark:text-gray-400 italic bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200 dark:border-yellow-800">
                                                    📝 {log.notes}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card.Content>
            </Card>
        );
    };

    // Render Venue Bookings
    const renderVenueBookings = () => (
        <div className="space-y-6">
            {/* Venue Management Section */}
            <Card>
                <Card.Header>
                    <div className="flex justify-between items-center">
                        <Card.Title className="text-xl font-serif flex items-center">
                            <FiHome className="mr-2" />
                            Manage Venues
                        </Card.Title>
                        <Button
                            onClick={() => setShowAddVenueModal(true)}
                            variant="primary"
                            icon={FiPlus}
                        >
                            Add Venue
                        </Button>
                    </div>
                </Card.Header>
                <Card.Content>
                    {venues.length === 0 ? (
                        <div className="text-center py-8">
                            <FiAlertCircle className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
                            <p className="font-literary text-primary-500 dark:text-gray-400">No venues found</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {venues.map((venue) => (
                                <div
                                    key={venue._id}
                                    className="p-4 bg-primary-50 dark:bg-gray-700/50 rounded-lg border border-primary-200 dark:border-gray-600"
                                >
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-serif font-medium text-primary-900 dark:text-white">
                                                    {venue.venue_unit}
                                                </h4>
                                                <Badge variant={venue.venue_status === 'available' ? 'success' : 'warning'} size="sm">
                                                    {venue.venue_status}
                                                </Badge>
                                            </div>
                                            <div className="text-sm text-primary-600 dark:text-gray-400 space-y-1">
                                                <p>Type: {venue.venue_type}</p>
                                                <p>Capacity: {venue.venue_capacity} people</p>
                                                <p>Rate: HKD ${venue.venue_hourly_rate}/hour</p>
                                                {venue.venue_description && (
                                                    <p className="text-xs text-primary-500 dark:text-gray-500 mt-2">
                                                        {venue.venue_description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => openEditVenueModal(venue)}
                                                variant="ghost"
                                                size="sm"
                                                icon={FiEdit}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                onClick={() => handleDeleteVenue(venue._id, venue.venue_unit)}
                                                variant="ghost"
                                                size="sm"
                                                icon={FiTrash2}
                                                className="text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300"
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Content>
            </Card>
            
            {/* Venue Bookings Section */}
            <Card>
                <Card.Header>
                    <Card.Title className="text-xl font-serif flex items-center">
                        <FiHome className="mr-2" />
                        Venue Bookings
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    {venueBookings.length === 0 ? (
                        <div className="text-center py-8">
                            <FiAlertCircle className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
                            <p className="font-literary text-primary-500 dark:text-gray-400">No venue bookings found</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {sortedVenueBookings.map((booking) => (
                                <div
                                    key={booking._id}
                                    className="p-4 bg-primary-50 dark:bg-gray-700/50 rounded-lg border border-primary-200 dark:border-gray-600"
                                >
                                    <div className="flex flex-col gap-4">
                                        {/* Header Section */}
                                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                                            <div className="flex-1 space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <FiHome className="text-primary-500" />
                                                    <h4 className="font-serif font-medium text-primary-900 dark:text-white">
                                                        {booking.venue?.venue_unit || 'Unknown Venue'}
                                                    </h4>
                                                </div>
                                                
                                                {/* Basic Info Grid */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-primary-600 dark:text-gray-400">
                                                    <p className="flex items-center gap-1">
                                                        <FiCalendar className="w-4 h-4" />
                                                        {formatDate(booking.booking_date || booking.venue_booking_date)}
                                                    </p>
                                                    <p className="flex items-center gap-1">
                                                        <FiClock className="w-4 h-4" />
                                                        {booking.booking_time} ({booking.booking_duration}h)
                                                    </p>
                                                    <p className="flex items-center gap-1">
                                                        <FiUser className="w-4 h-4" />
                                                        {booking.user?.full_name ||
                                                            `${booking.user?.First_Name || ''} ${booking.user?.Last_Name || ''}`.trim() ||
                                                            '—'}
                                                    </p>
                                                    {booking.total_pay > 0 && (
                                                        <p className="flex items-center gap-1">
                                                            <span>💰</span>
                                                            HKD ${booking.total_pay}
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                {/* Extended Booking Information */}
                                                {(booking.quantity || booking.reserve_small_room || booking.reserve_large_room || booking.affiliation || booking.remarks || booking.phone_number || booking.event_funding_source || booking.course_code || booking.booking_nature) && (
                                                    <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-md border border-primary-200 dark:border-gray-600">
                                                        <h5 className="text-xs font-serif font-semibold text-primary-900 dark:text-white mb-2 uppercase">
                                                            Booking Details
                                                        </h5>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-primary-600 dark:text-gray-400">
                                                            {booking.quantity && booking.quantity > 1 && (
                                                                <p><strong>Quantity:</strong> {booking.quantity}</p>
                                                            )}
                                                            {booking.reserve_small_room && (
                                                                <p><strong>Small Computer Room:</strong> Reserved (3 stations)</p>
                                                            )}
                                                            {booking.reserve_large_room && (
                                                                <p><strong>Large Computer Room:</strong> Reserved (5 stations)</p>
                                                            )}
                                                            {booking.affiliation && (
                                                                <p><strong>Affiliation:</strong> {booking.affiliation}</p>
                                                            )}
                                                            {booking.phone_number && (
                                                                <p><strong>Phone:</strong> {booking.phone_number}</p>
                                                            )}
                                                            {booking.event_funding_source && (
                                                                <p><strong>Funding Source:</strong> {booking.event_funding_source}</p>
                                                            )}
                                                            {booking.course_code && (
                                                                <p><strong>Course/Project:</strong> {booking.course_code}</p>
                                                            )}
                                                            {booking.account_code && (
                                                                <p><strong>Account Code:</strong> {booking.account_code}</p>
                                                            )}
                                                            {booking.booking_nature && (
                                                                <p><strong>Nature:</strong> {booking.booking_nature}</p>
                                                            )}
                                                        </div>
                                                        {booking.remarks && (
                                                            <div className="mt-2 pt-2 border-t border-primary-100 dark:border-gray-700">
                                                                <p className="text-xs"><strong>Remarks:</strong></p>
                                                                <p className="text-xs text-primary-600 dark:text-gray-400 mt-1">{booking.remarks}</p>
                                                            </div>
                                                        )}
                                                        {booking.booking_desc && (
                                                            <div className="mt-2 pt-2 border-t border-primary-100 dark:border-gray-700">
                                                                <p className="text-xs"><strong>Description:</strong></p>
                                                                <p className="text-xs text-primary-600 dark:text-gray-400 mt-1">{booking.booking_desc}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center">
                                                {renderStatusActions('venue', booking)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Content>
            </Card>

            {/* Venue Return Section */}
            <Card>
                <Card.Header>
                    <Card.Title className="text-xl font-serif flex items-center">
                        <FiCheck className="mr-2" />
                        Venue Return
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                Select Confirmed Venue Booking
                            </label>
                            <select
                                value={selectedVenueForReturn}
                                onChange={(e) => setSelectedVenueForReturn(e.target.value)}
                                className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-300"
                            >
                                <option value="">Select a confirmed venue booking...</option>
                                {confirmedVenueBookings.map((booking) => (
                                    <option key={booking._id} value={booking._id}>
                                        {booking.venue?.venue_unit} - {formatDate(booking.booking_date || booking.venue_booking_date)} at {booking.booking_time} ({booking.user?.First_Name} {booking.user?.Last_Name})
                                    </option>
                                ))}
                            </select>
                            {confirmedVenueBookings.length === 0 && (
                                <p className="text-xs text-primary-500 dark:text-gray-400 mt-2">
                                    No confirmed venue bookings available for return
                                </p>
                            )}
                        </div>
                        <Button
                            onClick={handleManualVenueReturn}
                            variant="primary"
                            className="w-full"
                            loading={returnLoading}
                            disabled={!selectedVenueForReturn || returnLoading}
                            icon={FiCheck}
                        >
                            Mark Venue as Returned
                        </Button>
                    </div>
                </Card.Content>
            </Card>
        </div>
    );

    if (loading) {
        return (
            <PageTemplate
                title="Manage Bookings"
                description="Admin interface for managing equipment and venue bookings"
                loading={true}
            />
        );
    }

    return (
        <PageTemplate
            title="Manage Bookings"
            description="Admin interface for managing equipment and venue bookings"
            icon="⚙️"
        >
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Tab Navigation */}
                <Card>
                    <Card.Content className="p-0">
                        <div className="flex flex-wrap border-b border-primary-200 dark:border-gray-700">
                            <Button
                                onClick={() => setActiveTab('bookings')}
                                variant={activeTab === 'bookings' ? 'primary' : 'ghost'}
                                className={`rounded-none border-0 ${
                                    activeTab === 'bookings' 
                                        ? 'border-b-2 border-primary-500' 
                                        : ''
                                }`}
                                icon={FiCalendar}
                            >
                                Bookings
                            </Button>
                            <Button
                                onClick={() => setActiveTab('equipment')}
                                variant={activeTab === 'equipment' ? 'primary' : 'ghost'}
                                className={`rounded-none border-0 ${
                                    activeTab === 'equipment' 
                                        ? 'border-b-2 border-primary-500' 
                                        : ''
                                }`}
                                icon={FiTool}
                            >
                                Equipment
                            </Button>
                            <Button
                                onClick={() => setActiveTab('return')}
                                variant={activeTab === 'return' ? 'primary' : 'ghost'}
                                className={`rounded-none border-0 ${
                                    activeTab === 'return' 
                                        ? 'border-b-2 border-primary-500' 
                                        : ''
                                }`}
                                icon={FiCamera}
                            >
                                Return Scanner
                            </Button>
                            <Button
                                onClick={() => setActiveTab('venue')}
                                variant={activeTab === 'venue' ? 'primary' : 'ghost'}
                                className={`rounded-none border-0 ${
                                    activeTab === 'venue' 
                                        ? 'border-b-2 border-primary-500' 
                                        : ''
                                }`}
                                icon={FiHome}
                            >
                                Venues
                            </Button>
                            <Button
                                onClick={() => setActiveTab('logs')}
                                variant={activeTab === 'logs' ? 'primary' : 'ghost'}
                                className={`rounded-none border-0 ${
                                    activeTab === 'logs' 
                                        ? 'border-b-2 border-primary-500' 
                                        : ''
                                }`}
                                icon={FiClock}
                            >
                                Activity Log
                            </Button>
                        </div>
                    </Card.Content>
                </Card>

                {/* Tab Content */}
                <div className="space-y-6">
                    {activeTab === 'bookings' && renderBookingsList()}
                    
                    {activeTab === 'equipment' && renderEquipmentByLocation()}
                    
                    {activeTab === 'return' && renderQrReturnSection()}
                    
                    {activeTab === 'venue' && renderVenueBookings()}
                    
                    {/* Activity Log is now a separate page, but keeping this for tab consistency if user navigates back */}
                    {activeTab === 'logs' && renderBookingLogs()}
                </div>
            </div>

            {/* Add Equipment Modal */}
            <Modal
                isOpen={showAddEquipmentModal}
                onClose={() => setShowAddEquipmentModal(false)}
                title="Add New Equipment"
            >
                <form onSubmit={handleAddEquipment} className="space-y-4">
                    <Input
                        label="Equipment Name"
                        value={newEquipmentData.eqm_name}
                        onChange={(e) => setNewEquipmentData({...newEquipmentData, eqm_name: e.target.value})}
                        required
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Category
                            </label>
                            <select
                                value={newEquipmentData.eqm_cat}
                                onChange={(e) => setNewEquipmentData({...newEquipmentData, eqm_cat: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                required
                            >
                                <option value="">Select Category</option>
                                {uniqueCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Type
                            </label>
                            <select
                                value={newEquipmentData.eqm_type}
                                onChange={(e) => setNewEquipmentData({...newEquipmentData, eqm_type: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                required
                            >
                                <option value="">Select Type</option>
                                {uniqueTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Location
                        </label>
                        <select
                            value={newEquipmentData.location}
                            onChange={(e) => setNewEquipmentData({...newEquipmentData, location: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        >
                            <option value="">Select Location</option>
                            {uniqueLocations.map(loc => (
                                <option key={loc} value={loc}>{loc}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                        </label>
                        <select
                            value={newEquipmentData.eqm_status}
                            onChange={(e) => setNewEquipmentData({...newEquipmentData, eqm_status: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        >
                            <option value="available">Available</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="out_of_order">Out of Order</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                        </label>
                        <textarea
                            value={newEquipmentData.eqm_description}
                            onChange={(e) => setNewEquipmentData({...newEquipmentData, eqm_description: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            rows="3"
                        />
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setShowAddEquipmentModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={addEquipmentLoading}
                        >
                            Add Equipment
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Edit Equipment Modal */}
            <Modal
                isOpen={showEditEquipmentModal}
                onClose={() => setShowEditEquipmentModal(false)}
                title="Edit Equipment"
            >
                {editEquipmentData && (
                    <form onSubmit={handleEditEquipment} className="space-y-4">
                        <Input
                            label="Equipment Name"
                            value={editEquipmentData.eqm_name}
                            onChange={(e) => setEditEquipmentData({...editEquipmentData, eqm_name: e.target.value})}
                            required
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Category
                                </label>
                                <select
                                    value={editEquipmentData.eqm_cat}
                                    onChange={(e) => setEditEquipmentData({...editEquipmentData, eqm_cat: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    {uniqueCategories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Type
                                </label>
                                <select
                                    value={editEquipmentData.eqm_type}
                                    onChange={(e) => setEditEquipmentData({...editEquipmentData, eqm_type: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                    required
                                >
                                    <option value="">Select Type</option>
                                    {uniqueTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Location
                            </label>
                            <select
                                value={editEquipmentData.location}
                                onChange={(e) => setEditEquipmentData({...editEquipmentData, location: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            >
                                <option value="">Select Location</option>
                                {uniqueLocations.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Status
                            </label>
                            <select
                                value={editEquipmentData.eqm_status}
                                onChange={(e) => setEditEquipmentData({...editEquipmentData, eqm_status: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            >
                                <option value="available">Available</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="out_of_order">Out of Order</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={editEquipmentData.eqm_description}
                                onChange={(e) => setEditEquipmentData({...editEquipmentData, eqm_description: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                rows="3"
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setShowEditEquipmentModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                loading={editEquipmentLoading}
                            >
                                Update Equipment
                            </Button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Add Venue Modal */}
            <Modal
                isOpen={showAddVenueModal}
                onClose={() => setShowAddVenueModal(false)}
                title="Add New Venue"
            >
                <form onSubmit={handleAddVenue} className="space-y-4">
                    <Input
                        label="Venue Unit"
                        value={newVenueData.venue_unit}
                        onChange={(e) => setNewVenueData({...newVenueData, venue_unit: e.target.value})}
                        required
                        placeholder="e.g., 4/F Meeting Room"
                    />
                    <Input
                        label="Venue Type"
                        value={newVenueData.venue_type}
                        onChange={(e) => setNewVenueData({...newVenueData, venue_type: e.target.value})}
                        required
                        placeholder="e.g., Meeting Room, Lab, Studio"
                    />
                    <Input
                        label="Capacity"
                        type="number"
                        value={newVenueData.venue_capacity}
                        onChange={(e) => setNewVenueData({...newVenueData, venue_capacity: e.target.value})}
                        required
                        placeholder="Number of people"
                    />
                    <Input
                        label="Hourly Rate (HKD)"
                        type="number"
                        value={newVenueData.venue_hourly_rate}
                        onChange={(e) => setNewVenueData({...newVenueData, venue_hourly_rate: parseFloat(e.target.value) || 0})}
                        required
                        placeholder="0"
                    />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                        </label>
                        <select
                            value={newVenueData.venue_status}
                            onChange={(e) => setNewVenueData({...newVenueData, venue_status: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        >
                            <option value="available">Available</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="out_of_order">Out of Order</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                        </label>
                        <textarea
                            value={newVenueData.venue_description}
                            onChange={(e) => setNewVenueData({...newVenueData, venue_description: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            rows="3"
                            placeholder="Optional description of the venue"
                        />
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setShowAddVenueModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            loading={addVenueLoading}
                        >
                            Add Venue
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Edit Venue Modal */}
            <Modal
                isOpen={showEditVenueModal}
                onClose={() => setShowEditVenueModal(false)}
                title="Edit Venue"
            >
                {editVenueData && (
                    <form onSubmit={handleEditVenue} className="space-y-4">
                        <Input
                            label="Venue Unit"
                            value={editVenueData.venue_unit}
                            onChange={(e) => setEditVenueData({...editVenueData, venue_unit: e.target.value})}
                            required
                        />
                        <Input
                            label="Venue Type"
                            value={editVenueData.venue_type}
                            onChange={(e) => setEditVenueData({...editVenueData, venue_type: e.target.value})}
                            required
                        />
                        <Input
                            label="Capacity"
                            type="number"
                            value={editVenueData.venue_capacity}
                            onChange={(e) => setEditVenueData({...editVenueData, venue_capacity: e.target.value})}
                            required
                        />
                        <Input
                            label="Hourly Rate (HKD)"
                            type="number"
                            value={editVenueData.venue_hourly_rate}
                            onChange={(e) => setEditVenueData({...editVenueData, venue_hourly_rate: parseFloat(e.target.value) || 0})}
                            required
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Status
                            </label>
                            <select
                                value={editVenueData.venue_status}
                                onChange={(e) => setEditVenueData({...editVenueData, venue_status: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            >
                                <option value="available">Available</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="out_of_order">Out of Order</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={editVenueData.venue_description}
                                onChange={(e) => setEditVenueData({...editVenueData, venue_description: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                rows="3"
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setShowEditVenueModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                loading={editVenueLoading}
                            >
                                Update Venue
                            </Button>
                        </div>
                    </form>
                )}
            </Modal>
        </PageTemplate>
    );
}

export default ManageBooking;
